import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { actions } from '@common/redux/modules/Actions';
import LatestSummary from '../../../component/templates/LatestSummary';
import { SearchCondition, SummaryApiResponse, summaryApi } from '@common/external/SummaryApi';
import { SearchConditionItem, SearchConditionState } from '@common/redux/reducers/SearchConditionReducer';
import { SCREEN, STATUS_TYPE } from '@common/Common';
import { initialNegotiationCountState, negotiationCountState } from '@common/redux/reducers/NegotiationCountReducer';
import { filterConditionState, initialCurrentFilterState } from '@common/redux/reducers/CurrentFilterReducer';
import { CustomCountProps } from '@common/redux/reducers/CustomCountListReducer';
import { LOADING_TYPE } from '@common/redux/reducers/LoadingStatusReducer';
import { CommonResult } from '@common/external/Common';
import { AgencyAppState } from '../../../redux/modules/store';
import { SORRY_MESSAGE_TYPE } from '@common/redux/reducers/SorryMessageReducer';
import { StoreAndAgencyInfo } from '@common/redux/reducers/StoreSelectorReducer';

const mapStateToProps = (appState: AgencyAppState) => {
  return {
    loadingStatus: appState.loadingStatus,
    loginUser: appState.loginUser,
    isLoggedIn: appState.loginUser.isLoggedIn,
    storeSelector: appState.storeSelector,
    header: {
      screenId: SCREEN.DASHBOARD.ID,
      screenName: SCREEN.DASHBOARD.NAME,
      statusType: STATUS_TYPE.NO_TYPE,
      loadingStatus: appState.loadingStatus.summary,
    },
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    fetchDashboardSummaryCount: (userId: string, targetStores: string[]) => {
      updateDashboardSummaryCount(userId, targetStores, dispatch);
    },
    updateRefresh: (userId: string, targetStores: string[]) => {
      const refresh = () => {
        console.log('current Refresh is Summary Dashboard');
        updateDashboardSummaryCount(userId, targetStores, dispatch);
      };
      dispatch(actions.updateRefresh({ refresh }));
    },
    setToggleModal: () => {
      dispatch(actions.storeSelector.toggleModal());
    },
    setToggleStore: (store: StoreAndAgencyInfo) => {
      store && dispatch(actions.storeSelector.toggleStore(store));
    },
  };
};

/**
 * 最新サマリとカスタムサマリの値を更新する
 * @param userId
 * @param targetStores
 * @param dispatch
 */
function updateDashboardSummaryCount(userId: string, targetStores: string[], dispatch: Dispatch) {
  dispatch(actions.updateLoadingStatus.started(LOADING_TYPE.SUMMARY));
  dispatch(actions.updateLoadingStatus.started(LOADING_TYPE.CONDITIONS));

  postSummary(userId, targetStores)
    .then((apiResultList) => {
      const customCountList = getCustomCountList(apiResultList);
      dispatch(actions.fetchCustomCountList({ customCountList }));

      const countNum = getSummaryCount(apiResultList);
      dispatch(actions.fetchNegotiationCount(countNum));

      const filterList = getFilterList(apiResultList);
      dispatch(actions.filterList.update(filterList));

      dispatch(
        actions.updateLoadingStatus.done({
          params: LOADING_TYPE.SUMMARY,
          result: apiResultList.commonResult,
        })
      );
      dispatch(
        actions.updateLoadingStatus.done({
          params: LOADING_TYPE.CONDITIONS,
          result: apiResultList.commonResult,
        })
      );
    })
    .catch((error) => {
      dispatch(actions.updateSorryMessage(SORRY_MESSAGE_TYPE.SYSTEM_ERROR));
      dispatch(
        actions.updateLoadingStatus.failed({
          params: LOADING_TYPE.SUMMARY,
          error: {},
        })
      );
      dispatch(
        actions.updateLoadingStatus.failed({
          params: LOADING_TYPE.CONDITIONS,
          error: {},
        })
      );
    });
}

export function getFilterList(input: apiResults): filterConditionState[] {
  const filterList: filterConditionState[] = [];
  input.results
    .filter((result) => result.filterConditions.filterKey)
    .forEach((result) => {
      filterList.push(result.filterConditions);
    });
  return filterList;
}

export interface apiResults {
  results: result[];
  commonResult?: CommonResult;
}
interface result {
  filterConditions: filterConditionState;
  countNum: { [key: string]: string | number };
}

export async function postSummary(userId: string, agencyCd: string[]): Promise<apiResults> {
  const response: SummaryApiResponse = await summaryApi({ userId: userId, agencyCd: agencyCd });

  const searchConditions = response.searchConditionResult;
  const apiResult: result[] = convertResponseToCustomCountProps(searchConditions);

  return {
    results: apiResult,
    commonResult: response.result,
  };
}

function convertResponseToCustomCountProps(apiResSearchConditions: SearchCondition[] | undefined): result[] {
  const resultList: result[] = [];
  apiResSearchConditions?.forEach((resSearchConditionItem: SearchCondition) => {
    const condition = buildConditionState(resSearchConditionItem);
    const total =
      resSearchConditionItem.summaryNumber.doing + resSearchConditionItem.summaryNumber.pending + resSearchConditionItem.summaryNumber.done;
    const apiResult = makeApiResult(resSearchConditionItem, condition, total);

    resultList.push(apiResult);
  });
  return resultList;
}

function buildConditionState(apiResSearchCondition: SearchCondition): SearchConditionState {
  const condition: SearchConditionState = [];

  apiResSearchCondition.searchCondition.searchBranch.forEach((branch) => {
    const searchConditionItem: SearchConditionItem = {
      searchConditionName: branch.branchConditionName,
      specifiedCondition: branch.specifiedCondition,
    };
    condition.push(searchConditionItem);
  });
  return condition;
}

function makeApiResult(apiResSearchCondition: SearchCondition, condition: SearchConditionState, total: number): result {
  let filterKey = '';
  if (apiResSearchCondition.searchCondition.searchConditionId !== 0) {
    filterKey = apiResSearchCondition.searchCondition.searchConditionId.toString();
  }
  return {
    filterConditions: {
      filterKey: filterKey,
      filterTitle: apiResSearchCondition.searchCondition.searchConditionName,
      searchConditions: condition,
    },
    countNum: {
      negotiationTotal: apiResSearchCondition.summaryNumber.negotiationTotal,
      customerTotal: apiResSearchCondition.summaryNumber.customerTotal,
      noneNegotiation: apiResSearchCondition.summaryNumber.noneNegotiation,
      noneProduct: apiResSearchCondition.summaryNumber.noneProduct,
      doing: apiResSearchCondition.summaryNumber.doing,
      pending: apiResSearchCondition.summaryNumber.pending,
      done: apiResSearchCondition.summaryNumber.done,
      customerDoing: apiResSearchCondition.summaryNumber.customerDoing,
      customerPending: apiResSearchCondition.summaryNumber.customerPending,
      customerDone: apiResSearchCondition.summaryNumber.customerDone,
    },
  };
}

export function getCustomCountList(input: apiResults): CustomCountProps[] {
  const output: CustomCountProps[] = [];

  input.results
    .filter((result) => {
      return result.filterConditions.filterKey !== '';
    })
    .forEach((result) => {
      output.push({ countNum: result.countNum, filterKey: result.filterConditions.filterKey });
    });
  return output;
}

export function getSummaryCount(input: apiResults): negotiationCountState {
  const FIRST = 0;
  const target = input.results.filter((result) => {
    return result.filterConditions.filterKey === '';
  });

  const countNum = target.length > 0 ? target[FIRST].countNum : makeInitialCustomCountProps().countNum;
  return {
    countNum: {
      negotiationTotal: countNum['negotiationTotal'],
      customerTotal: countNum['customerTotal'],
      noneNegotiation: countNum['noneNegotiation'],
      noneProduct: countNum['noneProduct'],
      doing: countNum['doing'],
      pending: countNum['pending'],
      done: countNum['done'],
      customerDoing: countNum['customerDoing'],
      customerPending: countNum['customerPending'],
      customerDone: countNum['customerDone'],
    },
  };
}

function makeInitialCustomCountProps(): CustomCountProps {
  return {
    filterKey: initialCurrentFilterState.filterKey,
    countNum: initialNegotiationCountState.countNum,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LatestSummary);
